<template>
    <div class="p-4">
        <v-loading v-if="isLoading"></v-loading>

        <!-- TABLE -->
        <DataTable 
            :datatable="items" 
            :header="headers" 
            @page-changed="pageChange($event)"
        >
            <!-- TOP BAR -->
            <template v-slot:table-slot>
                <div class="w-full grid grid-cols-[1fr_auto] gap-5">
                    
                    <!-- Search -->
                    <form @submit.prevent class="w-full">
                        <div class="flex items-center border-b bg-gray-100 py-2 px-3">
                            <input 
                                v-model="search"
                                type="text"
                                placeholder="Search..."
                                class="w-full bg-transparent focus:outline-none"
                            />
                            <mdicon name="magnify" size="24" class="text-gray-500" />
                        </div>
                    </form>

                    <!-- Add Button -->
                    <button 
                        v-if="isCreatePermitted"
                        class="bg-primary text-white px-4 py-2 rounded shadow hover:bg-primary/90"
                        @click="addModal"
                    >
                        Add
                    </button>

                </div>
            </template>

            <template v-slot:file="item">
                <a 
                    :href="item.item.path" 
                    target="_blank" 
                    class="text-primary underline flex items-center gap-1"
                >
                    <mdicon name="file-document-outline" size="18" />
                    {{ item.item.nama_file }}
                </a>
            </template>

            <!-- ACTION BUTTONS -->
            <template v-slot:action="item">
                <div class="flex gap-2">

                    <button 
                        v-if="isUpdatePermitted"
                        class="bg-primary text-white px-3 py-1 rounded text-xs shadow hover:bg-primary/90"
                        @click="editModal(item)"
                    >
                    <div class="flex justify-start items-center gap-2">
                        <mdicon name="pencil" size="14" />
                        <div>
                            Edit
                        </div>
                    </div>
                    </button>

                    <button 
                        v-if="isDeletePermitted"
                        class="bg-danger text-white px-3 py-1 rounded text-xs shadow hover:bg-red-700"
                        @click="deleteModal(item)"
                    >
                    <div class="flex justify-start items-center gap-2">
                        <mdicon name="trash-can-outline" size="14" /> 
                        <div>
                            Delete
                        </div>
                    </div>
                    </button>

                </div>
            </template>
        </DataTable>

        <!-- FORM MODAL -->
        <FormBase v-if="modal" @closeModal="modal = false">
            
            <template v-slot:formName>
                {{ title }}
            </template>

            <template v-slot:form>
                <div class="grid gap-6">

                    <!-- TYPE -->
                    <div>
                        <label class="font-medium text-sm mb-1 block">Type</label>
                        <select
                            v-model="form.type"
                            class="border rounded px-3 py-2 w-full focus:outline-none"
                        >
                            <option disabled value="">-- Pilih Type --</option>
                            <option 
                                v-for="opt in typeOptions" 
                                :key="opt.value" 
                                :value="opt.value"
                            >
                                {{ opt.label }}
                            </option>
                        </select>

                        <div 
                            v-if="selectedType.icon" 
                            class="flex items-center gap-2 mt-2 text-gray-700"
                        >
                            <mdicon :name="selectedType.icon" size="24" />
                            <span>{{ selectedType.label }}</span>
                        </div>
                    </div>

                    <!-- NAMA PRODUCT -->
                    <div>
                        <label class="font-medium text-sm">Nama Product</label>
                        <input 
                            v-model="form.nama_product"
                            type="text"
                            class="input"
                        />
                    </div>

                    <!-- ICON DROPDOWN CUSTOM -->
                    <div class="relative">
                        <label class="font-medium text-sm mb-1 block">Icon (mdi)</label>

                        <!-- Selected Display -->
                        <div 
                            @click="iconDropdown = !iconDropdown"
                            class="border rounded px-3 py-2 w-full cursor-pointer flex items-center justify-between"
                        >
                            <div class="flex items-center gap-2">
                                <mdicon v-if="form.icon" :name="form.icon" size="26" />
                                <span v-if="form.icon">{{ form.icon }}</span>
                                <span v-else class="text-gray-500">-- Pilih Icon --</span>
                            </div>
                            <span class="text-gray-500">▼</span>
                        </div>

                        <!-- Dropdown -->
                        <div 
                            v-if="iconDropdown"
                            class="absolute z-50 bg-white border shadow-lg rounded w-full max-h-60 overflow-y-auto mt-1"
                        >
                            <div 
                                v-for="ic in mdiIcons" 
                                :key="ic"
                                @click="selectIcon(ic)"
                                class="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                            >
                                <mdicon :name="ic" size="24" />
                                <span>{{ ic }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- FILE -->
                    <div>
                        <label class="font-medium text-sm">File</label>
                        <input 
                            type="file"
                            @change="selectFile($event)"
                            class="border p-2 rounded w-full"
                        />
                    </div>

                </div>
            </template>

            <template v-slot:formFooter>
                <div class="flex justify-end gap-3">
                    <button 
                        class="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                        @click="modal = false"
                    >
                        Cancel
                    </button>

                    <button 
                        class="px-4 py-2 rounded bg-primary text-white hover:bg-primary/90"
                        @click="save"
                    >
                        Save
                    </button>
                </div>
            </template>

        </FormBase>

        <!-- DELETE CONFIRM -->
        <Transition name="fade">
            <Confirm v-if="deletemodal" @closeconfirm="deletemodal = false">

                <template v-slot:header>
                    Confirm
                </template>

                <template v-slot:main>
                    Are you sure you want to delete this item?
                </template>

                <template v-slot:footer>
                    <div class="flex justify-end gap-3">
                        <LButton 
                            class="bg-gray-200 text-gray-800 px-4 py-2 rounded"
                            @click="deletemodal = false"
                        >
                            Cancel
                        </LButton>

                        <LButton 
                            class="bg-danger text-white px-4 py-2 rounded"
                            :disabled="isLoading"
                            @click="deleteData"
                        >
                            {{ isLoading ? "Loading..." : "Confirm" }}
                        </LButton>
                    </div>
                </template>

            </Confirm>
        </Transition>

    </div>
</template>

<script>
import LButton from '@/layouts/button.vue';
import Confirm from '@/layouts/confirm.vue';
import FormBase from '@/layouts/FormBase.vue';
import DataTable from '@/layouts/DataTable.vue';

export default {
    name: 'RiplayUmumTable',

    data() {
        return {
            isLoading: false,
            deletemodal: false,
            editIndex: -1,
            iconDropdown: false,

            deleteID: null,

            form: {
                id: "",
                type: "",
                nama_product: "",
                icon: "",
                file: null,
            },

            defaultForm: {
                id: "",
                type: "",
                nama_product: "",
                icon: "",
                file: null,
            },

            typeOptions: [
                { label: "Tabungan",  value: "tabungan",  icon: "bank" },
                { label: "Deposito",  value: "deposito",  icon: "safe" },
                { label: "Kredit",    value: "kredit",    icon: "credit-card" },
                { label: "Layanan",   value: "layanan",   icon: "help-circle" },
            ],

            mdiIcons: [
                "bank", "bank-outline", "wallet", "wallet-outline",
                "credit-card", "cash", "piggy-bank", "chart-line",
                "hand-coin", "currency-usd", "currency-btc",
                "file-document", "clipboard-list"
            ],

            headers: [],
            items: [],
            currentPage: 1,
            modal: false,
            search: "",
        };
    },

    computed: {
        title() {
            return this.editIndex > -1 ? "Edit Item" : "New Item";
        },

        selectedType() {
            return this.typeOptions.find(t => t.value === this.form.type) || {};
        }
    },

    mounted() {
        this.getData();
        this.getheader();
    },

    methods: {
        getheader(){
            if(this.isDeletePermitted == true || this.isUpdatePermitted == true){
                this.headers = [
                    {
                        Text : 'ID',
                        value : 'id',
                        type: 'string'
                    },
                    {
                        Text: 'Type',
                        value: 'type',
                        type: 'string'
                    },
                    {
                        Text: 'Product name',
                        value: 'nama_product',
                        type: 'string'
                    },
                    {
                        Text: 'File',
                        value : 'file',
                        slot : true
                    },
                    {
                        Text: 'Actions',
                        action: true,
                }
                ]
            }else{
                this.headers = [
                    {
                        Text : 'ID',
                        value : 'id',
                        type: 'string'
                    },
                    {
                        Text: 'Type',
                        value: 'type',
                        type: 'string'
                    },
                    {
                        Text: 'Product name',
                        value: 'nama_product',
                        type: 'string'
                    },
                    {
                        Text: 'Product File',
                        value: 'file',
                        type: 'url'
                    },
                    {
                        Text: 'File',
                        value : 'file',
                    },
                ]
            }
        },

        addModal() {
            this.clearform();
            this.modal = true;
        },

        editModal(val) {
            const obj = val.item;

            this.form.id = obj.id;
            this.form.type = obj.type;
            this.form.nama_product = obj.nama_product;
            this.form.icon = obj.icon;

            this.editIndex = 1;
            this.modal = true;
        },

        deleteModal(val) {
            this.deleteID = val.item.id;
            this.deletemodal = true;
        },

        selectIcon(ic) {
            this.form.icon = ic;
            this.iconDropdown = false;
        },

        selectFile(e) {
            this.form.file = e.target.files[0];
        },

         getData(){
            this.isLoading = true;

            this.axios.defaults.headers.common = { 'Authorization': `Bearer ${this.token}` }
            this.axios.get(`api/private/riplay-umum?page=${this.currentPage}`).then((response) => {
                this.items = response.data
            }).catch(error => {
               
              console.log(error)

            }).finally(() => {
                this.isLoading = false;

            })
        },

        save() {
            this.isLoading = true;

            let fd = new FormData();
            fd.append("type", this.form.type);
            fd.append("nama_product", this.form.nama_product);
            fd.append("icon", this.form.icon);

            if (this.form.file) {
                fd.append("file", this.form.file);
            }

            // Jika update → gunakan method spoofing
            let url = "/api/private/riplay-umum";
            if (this.editIndex > -1) {
                url = `/api/private/riplay-umum/${this.form.id}`;
                fd.append("_method", "PUT");
            }

            this.axios.defaults.headers.common = { 'Authorization': `Bearer ${this.token}` };

            this.axios.post(url, fd)
                .then(() => {
                    this.getData();
                    this.clearform();
                    this.modal = false;
                })
                .finally(() => this.isLoading = false);
        },

        deleteData() {
            this.isLoading = true;
            this.axios.defaults.headers.common = { 'Authorization': `Bearer ${this.token}` };
            this.axios
                .delete(`/api/private/riplay-umum/${this.deleteID}`)
                .then(() => {
                    this.getData();
                    this.deletemodal = false;
                })
                .finally(() => this.isLoading = false);
        },

        clearform() {
            this.form = JSON.parse(JSON.stringify(this.defaultForm));
            this.editIndex = -1;
        },

        pageChange(e) {
            this.currentPage = e;
            this.getData();
        }
    },

    components: { DataTable, FormBase, Confirm, LButton },
};
</script>

<style scoped>
.input {
    @apply w-full border-b border-gray-400 py-2 px-2 bg-transparent focus:outline-none;
}
</style>
